import { useCallback, useEffect, useMemo, useState } from "react";
import { apiFetch } from "../../../config/api";
import { formatDate } from "../../../config/utils";
import EmptyState from "../../../components/EmptyState";
import Loader from "../../../components/Loader";
import PageHeader from "../../../components/PageHeader";
import Pagination from "../../../components/Pagination";
import { ClientPortalBadge, formatMoney } from "../clientPortalUtils";

const PAGE_SIZE = 10;

export default function ClientInvoicesPage() {
  const [invoices, setInvoices] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);

  const loadLookups = useCallback(async () => {
    try {
      const res = await apiFetch("/api/client-portal/accounts");
      if (res.ok) setAccounts(await res.json());
    } catch {
      setAccounts([]);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      if (accountId) params.set("accountId", accountId);
      const res = await apiFetch(`/api/client-portal/invoices?${params.toString()}`);
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setInvoices(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.message || "Could not load client invoices.");
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => { loadLookups(); }, [loadLookups]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [accountId]);

  const totalPages = Math.max(1, Math.ceil(invoices.length / PAGE_SIZE));
  const pagedInvoices = useMemo(() => {
    const safePage = Math.min(page, totalPages);
    return invoices.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  }, [invoices, page, totalPages]);

  return (
    <div className="client-portal-page">
      <PageHeader
        title="Client Invoices"
        subtitle={`${invoices.length} linked invoice${invoices.length === 1 ? "" : "s"} in the current view.`}
      />

      <div className="crm-toolbar glass-card" style={{ marginBottom: 16 }}>
        <div className="crm-search">
          <select value={accountId} onChange={(event) => setAccountId(event.target.value)} aria-label="Filter by client">
            <option value="">All clients</option>
            {accounts.map((account) => <option key={account.id} value={account.id}>{account.name}</option>)}
          </select>
        </div>
      </div>

      {error && <div className="error-banner" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="glass-card client-portal-table-card">
        {loading ? (
          <div style={{ padding: 20 }}>
            {[1, 2, 3, 4, 5].map((item) => <Loader key={item} type="skeleton" height={48} style={{ marginBottom: 10 }} />)}
          </div>
        ) : invoices.length === 0 ? (
          <EmptyState
            title="No client invoices linked"
            subtitle="Finance invoices linked by client name, project, or CRM deal appear here."
          />
        ) : (
          <>
            <div className="client-portal-table-wrap">
              <table className="client-portal-table">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Client</th>
                    <th>Project</th>
                    <th>Issued</th>
                    <th>Due</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedInvoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td>
                        <div className="client-portal-row-title">{invoice.invoiceNumber}</div>
                        <div className="client-portal-row-subtitle">{invoice.crmDealId ? "Linked to CRM" : "Finance invoice"}</div>
                      </td>
                      <td>{invoice.clientName}</td>
                      <td>{invoice.projectName || "-"}</td>
                      <td>{formatDate(invoice.issueDate)}</td>
                      <td>{formatDate(invoice.dueDate)}</td>
                      <td><strong>{formatMoney(invoice.totalAmount, invoice.currency)}</strong></td>
                      <td><ClientPortalBadge status={invoice.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination page={Math.min(page, totalPages)} totalPages={totalPages} onPageChange={setPage} total={invoices.length} pageSize={PAGE_SIZE} />
          </>
        )}
      </div>
    </div>
  );
}
